/**
 * Rules for the freewriting tool: which edits the "keep going" mode refuses, how words
 * are counted, and how a session's goal is met.
 *
 * Freewriting, as Peter Elbow described it in *Writing Without Teachers* (1973), is
 * writing without stopping, not looking back, not crossing out and not worrying about
 * spelling. "No deleting" mode enforces the middle two by refusing the edits that
 * delete or replace text; it is a nudge a reader chooses, not a lock.
 */

export const GOAL_KINDS = ["minutes", "words", "none"] as const;
export type GoalKind = (typeof GOAL_KINDS)[number];

export const MINUTE_GOALS = [5, 10, 15, 20, 30] as const;
export const WORD_GOALS = [100, 250, 500, 750, 1000] as const;
export const IDLE_SECONDS = [5, 8, 12, 20] as const;

const WORD = /[\p{L}\p{N}][\p{L}\p{N}'’-]*/gu;

/** Words in `text`: runs of letters or digits, apostrophes and hyphens kept inside a word. */
export function countWords(text: string): number {
  return text.match(WORD)?.length ?? 0;
}

/** Input types that delete, undo, or bring in text from elsewhere. */
const DELETING = /^(?:delete|history)/;
const BRINGING_IN = new Set([
  "insertFromPaste",
  "insertFromPasteAsQuotation",
  "insertFromDrop",
  "insertFromYank",
  "insertReplacementText",
]);

/**
 * True when "no deleting" mode must cancel an edit: any deletion, undo or redo, any
 * text pasted or dropped in, and any typing over a selection (which would replace it).
 */
export function blocksEdit(inputType: string, hasSelection: boolean): boolean {
  if (DELETING.test(inputType) || BRINGING_IN.has(inputType)) {
    return true;
  }
  return hasSelection && inputType.startsWith("insert");
}

export interface Goal {
  kind: GoalKind;
  amount: number;
}

/** Whether the goal is met by this many session words and seconds. */
export function goalReached(
  goal: Goal,
  sessionWords: number,
  elapsedSeconds: number
): boolean {
  if (goal.kind === "words") {
    return sessionWords >= goal.amount;
  }
  if (goal.kind === "minutes") {
    return elapsedSeconds >= goal.amount * 60;
  }
  return false;
}

/** How far through the goal the session is, from 0 to 1; 0 for an open-ended session. */
export function goalProgress(
  goal: Goal,
  sessionWords: number,
  elapsedSeconds: number
): number {
  let done = 0;
  if (goal.kind === "words") {
    done = sessionWords / goal.amount;
  } else if (goal.kind === "minutes") {
    done = elapsedSeconds / (goal.amount * 60);
  }
  return Math.min(1, Math.max(0, done));
}

/** Words per minute for a session; 0 when no time has passed. */
export function writingPace(words: number, elapsedSeconds: number): number {
  return elapsedSeconds > 0 ? Math.round((words / elapsedSeconds) * 60) : 0;
}

/** Reads a saved draft; anything other than a string is an empty draft. */
export function parseSavedDraft(raw: string | null): string {
  if (!raw) {
    return "";
  }
  try {
    const data: unknown = JSON.parse(raw);
    return typeof data === "string" ? data.slice(0, 200_000) : "";
  } catch {
    return "";
  }
}
